import { useRef, type KeyboardEvent, type CSSProperties, type ReactNode } from "react";
import { useEmbedTokens } from "../theme";

export interface TabItem { id: string; label: ReactNode; }
interface Props {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  id?: string;
  "aria-label"?: string;
}

// Segmented tabs: a role="tablist" pill with roving tabindex + arrow-key nav.
export function EmbedTabs({ items, value, onChange, id, ...rest }: Props) {
  const t = useEmbedTokens();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const activate = (nextId: string) => { refs.current[nextId]?.focus(); onChange(nextId); };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const idx = items.findIndex((i) => i.id === value);
    const n = items.length;
    if (e.key === "ArrowRight") { e.preventDefault(); activate(items[(idx + 1) % n].id); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); activate(items[(idx - 1 + n) % n].id); }
    else if (e.key === "Home") { e.preventDefault(); activate(items[0].id); }
    else if (e.key === "End") { e.preventDefault(); activate(items[n - 1].id); }
  };
  const listStyle: CSSProperties = {
    display: "inline-flex", gap: 2, padding: 2, borderRadius: t.borderRadiusControl,
    background: t.colors.surfaceSunken, fontFamily: t.fontFamily,
    // Wrap, never overflow: a tab set whose pills exceed the container
    // (Refer & Save's four registry tabs inside the account shell's narrow
    // content column) grew the DOCUMENT's scrollWidth by 7px at 390px — a
    // horizontal page scroll, the exact defect the account shell's
    // minWidth:0 discipline exists to prevent. Inert wherever the pills fit,
    // so the two-tab consumers are unchanged; DOM order (and with it the
    // roving-tabindex arrow-key nav) is untouched by wrapping.
    flexWrap: "wrap",
  };
  const tabStyle = (active: boolean): CSSProperties => ({
    fontFamily: "inherit", fontSize: t.fontSize.sm, fontWeight: active ? t.fontWeight.medium : t.fontWeight.regular,
    // Task 12 check (c): a tab is a target - 24px on a mouse (it measured 23), 44px on touch.
    display: "inline-flex", alignItems: "center", minHeight: t.pointer === "fine" ? 24 : t.size.touchTarget,
    padding: `${t.spacing.xs}px ${t.spacing.md}px`, border: "none",
    borderRadius: Math.max(t.borderRadiusControl - 2, 2), cursor: "pointer",
    background: active ? t.colors.primaryTint : "transparent",
    color: active ? t.colors.link : t.colors.textSecondary,
  });
  return (
    <div role="tablist" aria-label={rest["aria-label"]} style={listStyle}>
      {items.map((it) => {
        const active = it.id === value;
        return (
          <button type="button" key={it.id} ref={(el) => { refs.current[it.id] = el; }}
            role="tab" id={id ? `${id}-${it.id}` : undefined}
            aria-selected={active} tabIndex={active ? 0 : -1}
            style={tabStyle(active)} onClick={() => onChange(it.id)} onKeyDown={onKeyDown}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
